import React, { useEffect, useRef } from 'react';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { hierarchySearch as hierarchySearchQuery } from '../../graphql/queries/search';
import { accountSearch, joinCodeSearch, ProDirectorySearch } from './graphql';
import AlbumSearchForm from './components/AlbumSearchForm';
import Results from './components/Results';
import {
  resetSearchQuery,
  setSearchQuery,
} from '../../redux/search/searchActions';
import {
  mapStateToHierarchySearchQueryVariables,
  isHierarchySearchQueryEmpty,
  isJoinCodeQueryEmpty,
  isUserQueryEmpty,
  isProDirectoryQueryEmpty,
} from './helpers';
import { createLoadMoreMethod } from '../../../../util/apollo';
import useComponentSize from '../../../../hooks/useComponentSize';
import useSearchMode from './useSearchMode';
import { SEARCH_MODES } from './components/AlbumSearchForm/AlbumSearchForm';
import { isAdmin } from '../../../../util/account';
import { MeQuery } from '../../../../graphql/queries/account';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  searchQuery: PropTypes.object,
  onSearchQuerySet: PropTypes.func,
  // results
  joinCodeAlbum: PropTypes.object,
  joinCodeAlbumFolder: PropTypes.object,
  hierarchySearch: PropTypes.shape({
    total: PropTypes.number,
    records: PropTypes.array,
  }),
  accountSearchResults: PropTypes.object,
  proDirectoryResults: PropTypes.object,
  // pagination, query states (loading/error)
  isLoading: PropTypes.bool,
  loadMore: PropTypes.func,
  error: PropTypes.object,
};

const AlbumSearch = ({
  account,
  searchQuery,
  onSearchQuerySet,
  isLoading = false,
  error,
  hierarchySearch,
  loadMore,
  joinCodeAlbum,
  joinCodeAlbumFolder,
  accountSearchResults,
  proDirectoryResults,
}) => {
  const ref = useRef();
  const { height } = useComponentSize(ref);

  const [currentSearchMode, setCurrentSearchMode] = useSearchMode();
  const hasQuery = !(() => {
    switch (currentSearchMode) {
      case SEARCH_MODES.JOIN_CODE:
        return isJoinCodeQueryEmpty(searchQuery);

      case SEARCH_MODES.EVENTS:
        return isHierarchySearchQueryEmpty(searchQuery);

      case SEARCH_MODES.LOCATION:
        return isHierarchySearchQueryEmpty(searchQuery);

      case SEARCH_MODES.USER:
        return isUserQueryEmpty(searchQuery);

      case SEARCH_MODES.PRO_DIRECTORY:
        return isProDirectoryQueryEmpty(searchQuery);

      default:
        // Default to true since we look for empty inside
        // this method.
        return true;
    }
  })();
  const hasResults = (() => {
    switch (currentSearchMode) {
      case SEARCH_MODES.JOIN_CODE:
        return !!joinCodeAlbum || !!joinCodeAlbumFolder;

      case SEARCH_MODES.EVENTS:
        return hierarchySearch?.total > 0;

      case SEARCH_MODES.LOCATION:
        return hierarchySearch?.total > 0;

      case SEARCH_MODES.USER:
        return accountSearchResults?.total > 0;

      case SEARCH_MODES.PRO_DIRECTORY:
        return proDirectoryResults?.total > 0;

      default:
        return false;
    }
  })();

  // Since we want to render this in an iframe (marcom website) we need
  // to notify the parent window of the app's height so that
  // it can properly set it when loading results.
  useEffect(() => {
    window?.parent?.postMessage?.(
      {
        height,
      },
      '*',
    );
  }, [height]);

  return (
    <div className="album-search-wrap" ref={ref}>
      <div className="album-search">
        <AlbumSearchForm
          canUseProDirectory={isAdmin(account)}
          onSearchQuerySet={onSearchQuerySet}
          query={searchQuery}
          searchMode={currentSearchMode}
          setSearchMode={setCurrentSearchMode}
        />

        <div className="album-search-results-wrap">
          <Results
            // search data
            currentSearchMode={currentSearchMode}
            hasQuery={hasQuery}
            searchQuery={searchQuery}
            // results
            hierarchySearch={hierarchySearch}
            joinCodeAlbum={joinCodeAlbum}
            joinCodeAlbumFolder={joinCodeAlbumFolder}
            accountSearchResults={accountSearchResults}
            proDirectoryResults={proDirectoryResults}
            // result meta
            hasError={!!error}
            hasResults={hasResults}
            isLoading={isLoading}
            loadMore={loadMore}
          />
        </div>
      </div>
    </div>
  );
};

AlbumSearch.propTypes = propTypes;

export { AlbumSearch as AlbumSearchComponent };

const mapStateToProps = ({ auth, galleries: { search } }) => {
  return {
    searchQuery: search.query,
    authenticated: auth.authenticated,
  };
};

const mapDispatchToProps = {
  onSearchQuerySet: setSearchQuery,
  onSearchQueryReset: resetSearchQuery,
};

const withAlbumSearchQuery = graphql(hierarchySearchQuery, {
  options: ({ searchQuery }) => ({
    variables: mapStateToHierarchySearchQueryVariables(searchQuery),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  }),
  props: ({
    data: { hierarchySearch, loading, error, fetchMore } = {},
    ownProps: { isLoading },
  }) => ({
    isLoading: loading || isLoading,
    hierarchySearch,
    error,
    loadMore: createLoadMoreMethod({
      source: hierarchySearch,
      recordsPath: '',
      fetchMore,
    }),
  }),
  skip: ({ searchQuery }) => isHierarchySearchQueryEmpty(searchQuery),
});

const withJoinCodeQuery = graphql(joinCodeSearch, {
  options: ({ searchQuery }) => ({
    variables: {
      code: searchQuery.join_code,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  }),
  props: ({
    data: { loading, joinCodeByCode, error },
    ownProps: { isLoading },
  }) => {
    return {
      isLoading: loading || isLoading,
      joinCodeAlbum: joinCodeByCode?.album,
      joinCodeAlbumFolder: joinCodeByCode?.album_folder,
      error,
    };
  },
  skip: ({ searchQuery }) => isJoinCodeQueryEmpty(searchQuery),
});

const withAccountSearchQuery = graphql(accountSearch, {
  options: ({ searchQuery }) => ({
    variables: {
      query: searchQuery.account_search_query,
      limit: searchQuery.accounts_limit,
      offset: searchQuery.accounts_offset,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  }),
  props: ({
    data: { loading, accountProfileSearch, error, fetchMore },
    ownProps: { isLoading },
  }) => {
    return {
      isLoading: loading || isLoading,
      accountSearchResults: accountProfileSearch?.items,
      error,
      loadMore: createLoadMoreMethod({
        source: accountProfileSearch,
        recordsPath: 'items',
        fetchMore,
      }),
    };
  },
  skip: ({ searchQuery }) => isUserQueryEmpty(searchQuery),
});

const withProDirectorySearch = graphql(ProDirectorySearch, {
  options: ({ searchQuery }) => ({
    variables: {
      location_point: {
        latitude: searchQuery.location.locationData.point.latitude,
        longitude: searchQuery.location.locationData.point.longitude,
      },
      limit: searchQuery.accounts_limit,
      offset: searchQuery.accounts_offset,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  }),
  props: ({
    data: { loading, proDirectorySearch, error, fetchMore },
    ownProps: { isLoading },
  }) => {
    return {
      isLoading: loading || isLoading,
      proDirectoryResults: proDirectorySearch?.items,
      error,
      loadMore: createLoadMoreMethod({
        source: proDirectorySearch,
        recordsPath: 'items',
        fetchMore,
      }),
    };
  },
  skip: ({ searchQuery }) => isProDirectoryQueryEmpty(searchQuery),
});

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    account: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withAlbumSearchQuery,
  withJoinCodeQuery,
  withAccountSearchQuery,
  withProDirectorySearch,
  withMeQuery,
)(AlbumSearch);
